import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Stage, Layer, Image as KImage, Line, Circle, Rect, Label, Tag, Text, Group } from "react-konva";
import { makeProjection, dist } from "@/editor/mercator";
import { useEditor } from "@/editor/store";
import { rect3, gutterAzimuth, rotateAbout, edgeAzimuth, splitRoof } from "@/editor/drawing";
import { computeLayout, makeFrame, obstacleShape, rectToLL, rafterLines } from "@/lib/geo";
import { fmt } from "@/lib/catalog";
import { Toolbar } from "@/editor/panels/Toolbar";
import { ToolRail } from "@/editor/panels/ToolRail";
import { LeftPanel } from "@/editor/panels/LeftPanel";
import { StructureLayer } from "@/editor/StructureLayer";
import { PlanMarks } from "@/editor/PlanMarks";
import { FieldLayer } from "@/editor/FieldLayer";
import { fieldsOf, editableFields, setFields, fitField, copyField, dropField } from "@/editor/fieldOps";
import { nextSectionLetter } from "@/lib/reportSection";
import { fileUrl } from "@/editor/panels/BackgroundPanel";
import { georefTransform } from "@/lib/reportPhoto";
import { runStructure, structSigOf } from "@/editor/panels/RoofTools";

import { tr } from "@/lib/i18n";
const TILE_RADIUS = 5;

const ZONE = { F: "#b45309", G: "#f59e0b", H: "#0F52BA", I: "#38bdf8" };

function useImg(url) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!url) return;
    const im = new window.Image();
    im.crossOrigin = "anonymous";
    im.src = url;
    im.onload = () => setImg(im);
    return () => { im.onload = null; };
  }, [url]);
  return img;
}

const Dim = ({ p1, p2, text, s = 1, onClick, listening = true }) => {
  const mx = (p1[0] + p2[0]) / 2, my = (p1[1] + p2[1]) / 2;
  return (
    <Label x={mx} y={my} scaleX={1 / s} scaleY={1 / s} onClick={onClick} onTap={onClick} listening={listening}
      onMouseEnter={(e) => { if (onClick) e.target.getStage().container().style.cursor = "text"; }}
      onMouseLeave={(e) => { e.target.getStage().container().style.cursor = ""; }}>
      <Tag fill="rgba(15,23,42,0.85)" cornerRadius={3} pointerDirection="none" />
      <Text text={text} fontSize={12} fontFamily="JetBrains Mono, monospace" fill="#fff" padding={4} />
    </Label>
  );
};

export default function RoofEditor({ p, update, phase = "roof", bom }) {
  const ed = useEditor();
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [hiddenDims, setHiddenDims] = useState(0);
  const panning = useRef(null);
  const dragField = useRef(null);

  // stato dell'editor isolato per progetto; strumento ripristinato a ogni cambio di fase
  useEffect(() => { if (useEditor.getState().pid !== p.id) useEditor.getState().resetFor(p.id); }, [p.id]);
  useEffect(() => { useEditor.getState().setTool("select"); }, [phase]);

  const prj = useMemo(() => makeProjection(p.lat || 46.0, p.lng || 8.0), [p.lat, p.lng]);
  const tiles = useMemo(() => prj.tiles(TILE_RADIUS), [prj]);

  const llToWorld = (ll) => prj.mToWorld(prj.toM(ll[0], ll[1]));
  const roofs = p.roofs || [];
  const activeRoof = roofs.find((r) => r.id === (ed.activeRoof || roofs[0]?.id)) || roofs[0];

  // fit view on mount / resize
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setSize({ w: r.width, h: r.height });
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // inquadra l'area dei tetti (o il centro se vuoto)
    let s = (Math.min(size.w, size.h) * 0.6) / prj.span(TILE_RADIUS) * 6;
    let cx = 0, cy = 0;
    const all = roofs.flatMap((r) => r.polygon || []);
    if (all.length) {
      const w = all.map((ll) => llToWorld(ll));
      const xs = w.map((q) => q[0]), ys = w.map((q) => q[1]);
      const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
      cx = (minx + maxx) / 2; cy = (miny + maxy) / 2;
      s = Math.min(size.w / (maxx - minx + 40), size.h / (maxy - miny + 40));
      s = Math.max(0.3, Math.min(6, s));
    } else {
      s = Math.max(0.6, Math.min(4, Math.min(size.w, size.h) / 300));
    }
    setView({ scale: s, x: size.w / 2 - cx * s, y: size.h / 2 - cy * s });
  }, [size.w, size.h]); // eslint-disable-line

  useEffect(() => {
    if (!ed.activeRoof && roofs[0]) ed.setActiveRoof(roofs[0].id);
  }, [roofs, ed]);

  // in fase "roof" garantisce sempre un campo attivo su cui disegnare
  useEffect(() => {
    if (phase === "roof" && roofs.length === 0) {
      const f = { id: `f${Date.now().toString(36)}`, name: tr("Campo 1"), polygon: [], pitch: p.roof_type === "piano" ? 0 : 20, azimuth: 0, obstacles: [], removed: [] };
      update({ roofs: [f] });
      ed.setActiveRoof(f.id);
    }
  }, [phase, roofs.length]); // eslint-disable-line

  const snapshot = () => ({ roofs: JSON.parse(JSON.stringify(p.roofs || [])), modules: { ...p.modules } });
  const commit = (patch) => { ed.pushHistory(snapshot()); update(patch); };

  // snap: vertici esistenti > lati esistenti > ortogonale (Shift) > griglia 10 cm
  const snapM = (m, evt) => {
    if (!ed.snap && !evt?.shiftKey) return m;
    const tol = 10 / (prj.ppm * view.scale);
    const verts = roofs.flatMap((r) => r.polygon.map((ll) => prj.toM(ll[0], ll[1])));
    if (ed.draft.length >= 3) verts.push(ed.draft[0]);
    let best = null, bd = tol;
    for (const v of verts) { const d = dist(v, m); if (d < bd) { bd = d; best = v; } }
    if (best) return best;
    if (evt?.shiftKey && ed.draft.length) {
      const l = ed.draft[ed.draft.length - 1];
      return Math.abs(m[0] - l[0]) > Math.abs(m[1] - l[1]) ? [m[0], l[1]] : [l[0], m[1]];
    }
    for (const r of roofs) {
      const pm = r.polygon.map((ll) => prj.toM(ll[0], ll[1]));
      for (let i = 0; i < pm.length && pm.length > 1; i++) {
        const a = pm[i], b = pm[(i + 1) % pm.length];
        const dx = b[0] - a[0], dy = b[1] - a[1], L = dx * dx + dy * dy;
        if (!L) continue;
        const t = Math.max(0, Math.min(1, ((m[0] - a[0]) * dx + (m[1] - a[1]) * dy) / L));
        const q = [a[0] + t * dx, a[1] + t * dy];
        if (dist(q, m) < tol * 0.8) return q;
      }
    }
    return ed.snap ? [Math.round(m[0] * 10) / 10, Math.round(m[1] * 10) / 10] : m;
  };

  const setEdgeLength = (rid, i, L) => {
    const r = roofs.find((x) => x.id === rid);
    if (!r || !(L > 0)) return;
    const pm = r.polygon.map((ll) => prj.toM(ll[0], ll[1]));
    const n = pm.length, a = pm[i], b = pm[(i + 1) % n];
    const cur = dist(a, b); if (!cur) return;
    const ux = (b[0] - a[0]) / cur, uy = (b[1] - a[1]) / cur, d = L - cur;
    const move = new Set([(i + 1) % n]);
    const c = pm[(i + 2) % n];
    if ((i + 2) % n !== i) {
      const ex = c[0] - b[0], ey = c[1] - b[1], el = Math.hypot(ex, ey) || 1;
      if (Math.abs((ex * ux + ey * uy) / el) < 0.25) move.add((i + 2) % n);
    }
    const np = pm.map((q, j) => (move.has(j) ? [q[0] + ux * d, q[1] + uy * d] : q)).map((q) => prj.toLL(q[0], q[1]));
    commit((prev) => ({ roofs: prev.roofs.map((x) => (x.id === rid ? { ...x, polygon: np, removed: [] } : x)) }));
  };

  const createRect = (W, Dp) => {
    if (!activeRoof || !(W > 0) || !(Dp > 0)) return;
    const c = prj.worldToM((size.w / 2 - view.x) / view.scale, (size.h / 2 - view.y) / view.scale);
    const az = ((Number(activeRoof.azimuth) || 0) * Math.PI) / 180, co = Math.cos(az), si = Math.sin(az);
    const toM = ([u, v]) => [c[0] + co * u + si * v, c[1] - si * u + co * v];
    const rm = [[-W / 2, -Dp / 2], [W / 2, -Dp / 2], [W / 2, Dp / 2], [-W / 2, Dp / 2]].map(toM);
    if (placeShape(rm)) return;
    const pts = rm.map((q) => prj.toLL(q[0], q[1]));
    commit((prev) => ({ roofs: prev.roofs.map((x) => (x.id === activeRoof.id ? { ...x, polygon: pts, removed: [] } : x)) }));
    ed.clearDraft(); ed.setTool("select");
  };

  // tetto a due falde / padiglione: il rettangolo dell'edificio sostituisce il campo attivo con le falde generate
  const placeShape = (rm) => {
    const shape = p.roof_shape || "monofalda";
    if (p.roof_type === "piano" || !["sella", "padiglione"].includes(shape)) return false;
    const sp = p.roof_shape_params || {};
    const parts = splitRoof(rm, shape, Number(sp.pitch) || 30, Number(sp.hip_pitch) || Number(sp.pitch) || 30);
    const gid = `g${Date.now().toString(36)}`;
    // gli ostacoli del campo sostituito passano alla falda che ne contiene il centro
    const obsC = (o) => (o.points ? o.points.map((ll) => prj.toM(ll[0], ll[1])).reduce((a, q, _, A) => [a[0] + q[0] / A.length, a[1] + q[1] / A.length], [0, 0]) : prj.toM(o.lat, o.lng));
    const olds = activeRoof?.obstacles || [];
    const falde = parts.map((q, i) => {
      const az = gutterAzimuth(q.pts);
      return { id: `${gid}${i}`, group: gid, name: tr("Falda {0}", [CARD(az)]), polygon: q.pts.map((m) => prj.toLL(m[0], m[1])), pitch: q.pitch, azimuth: az, gutter_edge: 0,
        obstacles: olds.filter((o) => pointInPoly(obsC(o), q.pts)), removed: [] };
    });
    commit((prev) => ({ roofs: [...(prev.roofs || []).filter((r) => r.id !== activeRoof?.id), ...falde] }));
    ed.setActiveRoof(falde[0].id); ed.clearDraft(); ed.setTool("select");
    return true;
  };

  const ptrM = () => {
    const st = stageRef.current;
    const pt = st.getPointerPosition();
    const wx = (pt.x - view.x) / view.scale, wy = (pt.y - view.y) / view.scale;
    return prj.worldToM(wx, wy);
  };

  const onWheel = (e) => {
    e.evt.preventDefault();
    const st = stageRef.current;
    const old = view.scale;
    const pt = st.getPointerPosition();
    const by = e.evt.deltaY > 0 ? 0.9 : 1.1;
    const ns = Math.max(0.1, Math.min(12, old * by));
    const mx = (pt.x - view.x) / old, my = (pt.y - view.y) / old;
    setView({ scale: ns, x: pt.x - mx * ns, y: pt.y - my * ns });
  };

  const onMouseDown = (e) => {
    if (e.evt.button === 2 || e.evt.button === 1 || ed.tool === "pan") {
      panning.current = { x: e.evt.clientX, y: e.evt.clientY, vx: view.x, vy: view.y };
      return;
    }
    if (e.evt.button !== 0) return;
    const m = phase === "roof" ? snapM(ptrM(), e.evt) : ptrM();
    if (phase === "modules" && ed.tool === "move-field") {
      const f = layout.fields.find((x) => pointInPoly(m, x.roof.polygon.map((ll) => prj.toM(ll[0], ll[1])))) || layout.fields[0];
      if (!f) return;
      ed.pushHistory(snapshot());
      dragField.current = { fid: f.roof.id, frame: f.frame, start: f.frame.toUV(prj.toLL(m[0], m[1])), ou: Number(f.roof.off_u) || 0, ov: Number(f.roof.off_v) || 0 };
      return;
    }
    if (ed.tool === "georef" && ed.georef) {
      const ll = [...ed.georef.ll, prj.toLL(m[0], m[1])];
      if (ll.length < 2) { ed.setGeoref({ ...ed.georef, ll }); return; }
      const a = prj.toM(ll[0][0], ll[0][1]);
      if (dist(a, m) < 1) { toast.error(tr("Scegli due punti sulla mappa distanti almeno 1 m")); return; }
      update((prev) => ({ background: { ...(prev.background || {}), px: ed.georef.px, ll } }));
      ed.setGeoref(null); ed.setTool("select");
      return;
    }
    if (phase === "roof" && ed.tool === "draw-rect3") {
      if (!activeRoof) { addRoof(); return; }
      if (ed.draft.length < 2) ed.addVertex(m);
      else closeRoof(rect3(ed.draft[0], ed.draft[1], m));
      return;
    }
    if (phase === "roof" && ed.tool === "draw-roof") {
      if (!activeRoof) { addRoof(); return; }
      if (ed.draft.length >= 3 && dist(m, ed.draft[0]) * prj.ppm * view.scale < 12) return closeRoof();
      ed.addVertex(m);
    } else if (phase === "roof" && ed.tool === "draw-obstacle" && activeRoof) {
      if (ed.draft.length === 0) ed.addVertex(m);
      else closeObstacle(m);
    } else if (phase === "roof" && (ed.tool === "draw-obstacle-poly" || ed.tool === "draw-obstacle-line") && activeRoof) {
      if (ed.tool === "draw-obstacle-poly" && ed.draft.length >= 3 && dist(m, ed.draft[0]) * prj.ppm * view.scale < 12) return closeShapeObstacle();
      ed.addVertex(m);
    } else if (phase === "modules" && ed.tool === "draw-field") {
      if (ed.draft.length >= 3 && dist(m, ed.draft[0]) * prj.ppm * view.scale < 12) return closeField();
      ed.addVertex(m);
    } else if (phase === "modules" && ed.tool === "delete-module") {
      toggleModuleAt(m);
    } else if (phase === "modules" && ed.tool === "section") {
      const f = layout.fields.find((x) => pointInPoly(m, x.roof.polygon.map((ll) => prj.toM(ll[0], ll[1]))));
      if (!f) { toast.error(tr("Clicca all'interno di una falda")); return; }
      const u = f.frame.toUV(prj.toLL(m[0], m[1]))[0];
      commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === f.roof.id ? { ...r, section: { u, letter: nextSectionLetter(prev.roofs, r.id) } } : r)) }));
      ed.setTool("select");
    }
  };

  const onMouseMove = (e) => {
    if (panning.current) {
      setView((v) => ({ ...v, x: panning.current.vx + (e.evt.clientX - panning.current.x), y: panning.current.vy + (e.evt.clientY - panning.current.y) }));
      return;
    }
    if (dragField.current) {
      const d = dragField.current, m = ptrM();
      const uv = d.frame.toUV(prj.toLL(m[0], m[1]));
      update((prev) => ({ roofs: prev.roofs.map((r) => (r.id === d.fid ? { ...r, off_u: d.ou + uv[0] - d.start[0], off_v: d.ov + uv[1] - d.start[1], removed: [] } : r)) }));
      return;
    }
    if (ed.draft.length) ed.setHover(snapM(ptrM(), e.evt));
  };
  const onMouseUp = () => { panning.current = null; dragField.current = null; };

  const closeRoof = (pts) => {
    const draft = pts || useEditor.getState().draft;
    if (draft.length < 3) return;
    if (pts && placeShape(pts)) return;
    const poly = draft.map((m) => prj.toLL(m[0], m[1]));
    const az = gutterAzimuth(draft);
    commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === activeRoof.id ? { ...r, polygon: poly, azimuth: az, gutter_edge: 0, removed: [] } : r)) }));
    ed.clearDraft(); ed.setTool("select");
  };
  // azioni sul campo moduli selezionato: adatta, copia, rimuovi
  const fieldAction = (kind) => {
    const sf = ed.selField, f = sf && layout.fields.find((x) => x.roof.id === sf.rid);
    const polys = f ? editableFields(f.roof) : [];
    if (!f || !polys[sf.i]) { toast.error(tr("Seleziona prima un campo moduli con un clic")); return; }
    const put = (fields, touched) => commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === f.roof.id ? setFields(r, fields, touched) : r)) }));
    if (kind === "fit") {
      const poly = fitField(f, sf.i);
      if (!poly) { toast.error(tr("Il campo non contiene moduli")); return; }
      put(polys.map((a, j) => (j === sf.i ? poly : a)), sf.i);
    } else if (kind === "copy") {
      const c = copyField(f, polys[sf.i], polys.filter((_, j) => j !== sf.i));
      put([...polys, c.poly], polys.length);
      ed.setSelField({ rid: f.roof.id, i: polys.length });
      if (!c.free) toast.warning(tr("Nessuno spazio libero accanto: la copia è sovrapposta, trascinala dove serve"));
    } else if (kind === "delete") {
      commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === f.roof.id ? dropField(r, editableFields(r), sf.i) : r)) }));
      ed.setSelField(null);
    }
  };

  // campo moduli parziale: area disegnata dentro il tetto che la contiene
  const closeField = () => {
    const d = useEditor.getState().draft;
    if (d.length < 3) return;
    const host = roofs.find((r) => r.polygon.length >= 3 && pointInPoly(d[0], r.polygon.map((ll) => prj.toM(ll[0], ll[1])))) || activeRoof;
    if (!host) return;
    const area = d.map((q) => prj.toLL(q[0], q[1]));
    const n = fieldsOf(host).length;
    commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === host.id ? setFields(r, [...fieldsOf(r), area], n) : r)) }));
    ed.setActiveRoof(host.id); ed.setSelField({ rid: host.id, i: n }); ed.clearDraft(); ed.setTool("select");
  };
  const closeShapeObstacle = () => {
    const d = useEditor.getState().draft, poly = ed.tool === "draw-obstacle-poly";
    if (d.length < (poly ? 3 : 2)) return;
    const o = { id: Date.now().toString(36), type: poly ? "poly" : "line", name: `${poly ? tr("Area esclusa") : tr("Linea")} ${(activeRoof.obstacles || []).length + 1}`,
      points: d.map((q) => prj.toLL(q[0], q[1])), clearance: poly ? 0.3 : 0.5, height: 0 };
    commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === activeRoof.id ? { ...r, obstacles: [...(r.obstacles || []), o], removed: [] } : r)) }));
    ed.clearDraft(); ed.setTool("select");
  };
  const closeObstacle = (m2) => {
    const [a, b] = [ed.draft[0], m2];
    const fr = makeFrame(activeRoof.polygon, Number(activeRoof.azimuth) || 0);
    const ua = fr.toUV(prj.toLL(a[0], a[1])), ub = fr.toUV(prj.toLL(b[0], b[1]));
    const w = Math.round((Math.abs(ub[0] - ua[0]) || 1) * 100) / 100, h = Math.round((Math.abs(ub[1] - ua[1]) || 1) * 100) / 100;
    const ll = fr.toLL([(ua[0] + ub[0]) / 2, (ua[1] + ub[1]) / 2]);
    const o = { id: Date.now().toString(36), type: "rect", name: tr("Camino {0}", [(activeRoof.obstacles || []).length + 1]), lat: ll[0], lng: ll[1], w, h, clearance: null, height: 1 };
    commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === activeRoof.id ? { ...r, obstacles: [...(r.obstacles || []), o], removed: [] } : r)) }));
    ed.clearDraft(); ed.setTool("select");
  };

  const layout = useMemo(() => computeLayout(p), [p]);

  const toggleModuleAt = (m) => {
    const tgt = prj.mToWorld(m);
    for (const f of layout.fields) {
      for (const c of f.cells) {
        const corners = rectToLL(c.rect, f.frame).map(llToWorld);
        if (pointInPoly(tgt, corners)) {
          commit((prev) => ({
            roofs: prev.roofs.map((r) => {
              if (r.id !== c.fid) return r;
              const s = new Set(r.removed || []);
              s.has(c.key) ? s.delete(c.key) : s.add(c.key);
              return { ...r, removed: [...s] };
            }),
          }));
          return;
        }
      }
    }
  };

  const rotRef = useRef(null);
  const rotateRoof = (node) => {
    const R0 = rotRef.current; if (!R0) return;
    const m = prj.worldToM(node.x(), node.y());
    const a = Math.atan2(m[1] - R0.c[1], m[0] - R0.c[0]) - Math.PI / 2;
    const rot = (ll) => { const q = rotateAbout(prj.toM(ll[0], ll[1]), R0.c, a); return prj.toLL(q[0], q[1]); };
    const b = R0.base, poly = b.polygon.map(rot);
    const pm = poly.map((ll) => prj.toM(ll[0], ll[1]));
    const az = edgeAzimuth(pm, (Number(b.gutter_edge) || 0) % pm.length);
    update((prev) => ({ roofs: prev.roofs.map((x) => (x.id !== b.id ? x : {
      ...x, polygon: poly, azimuth: az, removed: [],
      module_area: b.module_area ? b.module_area.map(rot) : b.module_area,
      module_fields: b.module_fields ? b.module_fields.map((a) => a.map(rot)) : b.module_fields,
      obstacles: (b.obstacles || []).map((o) => (o.points ? { ...o, points: o.points.map(rot) } : (() => { const ll = rot([o.lat, o.lng]); return { ...o, lat: ll[0], lng: ll[1] }; })())),
    })) }));
  };
  const structSig = structSigOf(p);
  const structStale = !!ed.struct && ed.struct.sig !== structSig;

  const toggleCell = (c) => commit((prev) => ({
    roofs: prev.roofs.map((r) => {
      if (r.id !== c.fid) return r;
      const s = new Set(r.removed || []);
      s.has(c.key) ? s.delete(c.key) : s.add(c.key);
      return { ...r, removed: [...s] };
    }),
  }));

  // i vertici condivisi tra falde dello stesso edificio si spostano insieme
  const moveVertex = (rid, i, node) => {
    const m = prj.worldToM(node.x(), node.y());
    const ll = prj.toLL(m[0], m[1]);
    update((prev) => {
      const src = prev.roofs.find((r) => r.id === rid);
      const old = src ? prj.toM(src.polygon[i][0], src.polygon[i][1]) : null;
      const same = (q) => old && dist(prj.toM(q[0], q[1]), old) < 0.01;
      return { roofs: prev.roofs.map((r) => {
        if (r.id === rid) return { ...r, polygon: r.polygon.map((q, j) => (j === i ? ll : q)), removed: [] };
        if (!src?.group || r.group !== src.group || !r.polygon.some(same)) return r;
        return { ...r, polygon: r.polygon.map((q) => (same(q) ? ll : q)), removed: [] };
      }) };
    });
  };

  // keyboard
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") { ed.clearDraft(); ed.setSelField(null); if (ed.tool === "georef") { ed.setGeoref(null); ed.setTool("select"); } }
      if ((e.ctrlKey || e.metaKey) && e.key === "z") { const s = ed.undo(snapshot()); if (s) update(s); }
      if ((e.ctrlKey || e.metaKey) && e.key === "y") { const s = ed.redo(snapshot()); if (s) update(s); }
      if ((e.key === "Delete" || e.key === "Backspace") && phase === "modules" && ed.selField && !/INPUT|SELECT|TEXTAREA/.test(document.activeElement?.tagName || "")) { fieldAction("delete"); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && ed.selId && !/INPUT|SELECT|TEXTAREA/.test(document.activeElement?.tagName || "")) deleteSelected();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }); // eslint-disable-line

  const addRoof = () => {
    const f = { id: `f${Date.now().toString(36)}`, name: tr("Campo {0}", [roofs.length + 1]), polygon: [], pitch: p.roof_type === "piano" ? 0 : 20, azimuth: 0, obstacles: [], removed: [] };
    commit((prev) => ({ roofs: [...(prev.roofs || []), f] }));
    ed.setActiveRoof(f.id); ed.setTool("draw-roof");
  };
  const deleteSelected = (id = useEditor.getState().selId) => {
    if (!id) return;
    const rest = roofs.filter((r) => r.id !== id);
    if (rest.length !== roofs.length) { commit({ roofs: rest }); ed.setActiveRoof(rest[0]?.id || null); }
    else commit((prev) => ({ roofs: prev.roofs.map((r) => ({ ...r, obstacles: (r.obstacles || []).filter((o) => o.id !== id) })) }));
    ed.select(null);
  };

  const exportPNG = () => {
    const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
    const a = document.createElement("a"); a.href = uri; a.download = `${p.name || "tetto"}.png`; a.click();
  };
  const exportJSON = () => {
    const data = { name: p.name, address: p.address, lat: p.lat, lng: p.lng, roof_type: p.roof_type, roofs: p.roofs, modules: p.modules, env: p.env };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${p.name || "progetto"}.json`; a.click();
  };

  if (!p.lat) {
    return (
      <div className="h-full grid place-items-center sr-grid-bg" data-testid="editor-no-location">
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center max-w-sm">
          <div className="font-display text-lg font-semibold">{tr("Nessuna posizione")}</div>
          <p className="text-sm text-slate-500 mt-1">{tr("Torna al passo \"Dati di progetto\" e inserisci l'indirizzo per caricare la vista satellitare.")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid={`editor-${phase}`}>
      <Toolbar ed={ed} view={view} setView={setView} onExportPNG={exportPNG} onExportJSON={exportJSON}
        onUndo={() => { const s = ed.undo(snapshot()); if (s) update(s); }} onRedo={() => { const s = ed.redo(snapshot()); if (s) update(s); }} />
      <div className="flex-1 min-h-0 flex">
        <LeftPanel p={p} update={update} commit={commit} ed={ed} phase={phase} roofs={roofs} activeRoof={activeRoof} layout={layout} bom={bom} addRoof={addRoof} deleteSelected={deleteSelected} setEdgeLength={setEdgeLength} createRect={createRect} />
        <div ref={wrapRef} className="flex-1 relative bg-slate-900 min-h-[300px]" data-testid="editor-canvas-wrap"
          onContextMenu={(e) => e.preventDefault()}
          style={{ cursor: ed.tool.startsWith("draw") || ed.tool === "georef" || ed.tool === "section" ? "crosshair" : ed.tool === "delete-module" ? "cell" : ed.tool === "move-field" ? "move" : ed.tool === "pan" ? "grab" : "default" }}>
          <Stage ref={stageRef} width={size.w} height={size.h} scaleX={view.scale} scaleY={view.scale} x={view.x} y={view.y}
            onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
            onDblClick={() => {
              const tl = ed.tool;
              if (!["draw-roof", "draw-obstacle-poly", "draw-obstacle-line", "draw-field"].includes(tl)) return;
              const d = useEditor.getState().draft;
              if (d.length < 2) return;
              // chiude solo se i due clic coincidono (vero doppio clic); rimuove il vertice duplicato
              if (dist(d[d.length - 1], d[d.length - 2]) * prj.ppm * view.scale > 6) return;
              useEditor.getState().popVertex();
              if (tl === "draw-roof") closeRoof();
              else if (tl === "draw-field") closeField();
              else closeShapeObstacle();
            }}>
            <Layer listening={false}>
              {ed.showBg
                ? tiles.map((t) => <TileImg key={t.key} t={t} />)
                : <Rect x={-2000} y={-2000} width={4000} height={4000} fill="#1e293b" />}
              {p.background?.file_id && p.background.visible !== false && <BgPhoto bg={p.background} llToWorld={llToWorld} ppm={prj.ppm} onError={ed.setBgError}
                center={(() => { const all = roofs.flatMap((r) => r.polygon || []); return llToWorld(all.length ? [all.reduce((t, q) => t + q[0], 0) / all.length, all.reduce((t, q) => t + q[1], 0) / all.length] : [p.lat, p.lng]); })()} />}
              {ed.tool === "georef" && (ed.georef?.ll || []).map((ll, i) => { const q = llToWorld(ll); return <Circle key={i} x={q[0]} y={q[1]} radius={7 / view.scale} fill="#f59e0b" stroke="#fff" strokeWidth={2 / view.scale} />; })}
            </Layer>
            <Layer>
              {/* altri tetti */}
              {roofs.map((r) => {
                if (r.polygon.length < 3) return null;
                const pts = r.polygon.map(llToWorld);
                const isAct = r.id === activeRoof?.id;
                return (
                  <Group key={r.id}>
                    <Line points={pts.flat()} closed stroke={isAct ? "#38bdf8" : "#94a3b8"} strokeWidth={2 / view.scale}
                      fill={isAct ? "rgba(15,82,186,0.18)" : "rgba(148,163,184,0.15)"}
                      onMouseDown={(e) => { if (ed.tool === "select") { e.cancelBubble = true; ed.setActiveRoof(r.id); ed.select(r.id); } }} />
                    {pts.length >= 2 && (() => { const g = (Number(r.gutter_edge) || 0) % pts.length; return <Line points={[...pts[g], ...pts[(g + 1) % pts.length]]} stroke="#f59e0b" strokeWidth={3.5 / view.scale} listening={false} />; })()}
                  </Group>
                );
              })}

              {/* maniglie dei vertici del tetto attivo, sopra a tutte le falde */}
              {activeRoof && ed.tool === "select" && activeRoof.polygon.length >= 3 && activeRoof.polygon.map(llToWorld).map((pt, i) => (
                <Circle key={`v-${activeRoof.id}-${i}`} x={pt[0]} y={pt[1]} radius={6 / view.scale} fill="#fff" stroke="#0F52BA" strokeWidth={2 / view.scale}
                  draggable={phase === "roof"} onDragStart={() => ed.pushHistory(snapshot())} onDragMove={(e) => moveVertex(activeRoof.id, i, e.target)} />
              ))}

              {/* ostacoli: rettangolo / poligono / linea, fascia di rispetto per ostacolo */}
              {roofs.map((r) => {
                if (r.polygon.length < 3) return null;
                const fr = makeFrame(r.polygon, Number(r.azimuth) || 0);
                return (r.obstacles || []).map((o) => {
                  const sh = obstacleShape(o, fr);
                  const pts = sh.pts.map((q) => llToWorld(fr.toLL(q)));
                  const c = o.clearance === null || o.clearance === undefined || o.clearance === "" ? Number(p.modules?.obstacle_clearance) || 0 : Number(o.clearance);
                  const sel = ed.selId === o.id;
                  return (
                    <Group key={o.id}>
                      {c > 0 && <Line points={pts.flat()} closed={sh.closed} stroke="rgba(245,158,11,0.28)" strokeWidth={2 * c * prj.ppm} lineJoin="round" lineCap="round" listening={false} />}
                      <Line points={pts.flat()} closed={sh.closed} fill={sh.closed ? (sel ? "rgba(245,158,11,0.8)" : "rgba(245,158,11,0.55)") : undefined}
                        stroke="#f59e0b" strokeWidth={(sh.closed ? 1.5 : 3) / view.scale} hitStrokeWidth={12 / view.scale}
                        draggable={phase === "roof" && ed.tool === "select"}
                        onMouseDown={(e) => { if (ed.tool === "select") { e.cancelBubble = true; ed.select(o.id); ed.setActiveRoof(r.id); } }}
                        onDragStart={() => ed.pushHistory(snapshot())}
                        onDragEnd={(e) => {
                          const n = e.target, dx = n.x() / prj.ppm, dy = -n.y() / prj.ppm;
                          const mv = (ll) => { const m0 = prj.toM(ll[0], ll[1]); return prj.toLL(m0[0] + dx, m0[1] + dy); };
                          n.position({ x: 0, y: 0 });
                          update((prev) => ({ roofs: prev.roofs.map((x) => (x.id === r.id ? { ...x, removed: [], obstacles: x.obstacles.map((q) => {
                            if (q.id !== o.id) return q;
                            if (q.points) return { ...q, points: q.points.map(mv) };
                            const ll = mv([q.lat, q.lng]); return { ...q, lat: ll[0], lng: ll[1] };
                          }) } : x)) }));
                        }} />
                    </Group>
                  );
                });
              })}

              {/* area "Campo moduli" parziale */}
              {phase !== "modules" && roofs.map((r) => fieldsOf(r).map((a, i) => (
                <Line key={`ma-${r.id}-${i}`} points={a.map(llToWorld).flat()} closed stroke="#22d3ee" strokeWidth={2 / view.scale} dash={[8 / view.scale, 5 / view.scale]} listening={false} />
              )))}

              {/* maniglia di rotazione del tetto selezionato */}
              {phase === "roof" && ed.tool === "select" && activeRoof && ed.selId === activeRoof.id && activeRoof.polygon.length >= 3 && (() => {
                const pm = activeRoof.polygon.map((ll) => prj.toM(ll[0], ll[1]));
                const c = [pm.reduce((t, q) => t + q[0], 0) / pm.length, pm.reduce((t, q) => t + q[1], 0) / pm.length];
                const rad = Math.max(...pm.map((q) => dist(q, c))) + 24 / (prj.ppm * view.scale);
                const cw = prj.mToWorld(c), hp0 = prj.mToWorld([c[0], c[1] + rad]);
                const topW = (18 - view.y) / view.scale;
                const hp = [hp0[0], Math.min(cw[1] - 14 / view.scale, Math.max(hp0[1], topW))];
                return (
                  <Group>
                    <Line points={[...cw, ...hp]} stroke="#38bdf8" strokeWidth={1 / view.scale} dash={[4 / view.scale, 4 / view.scale]} listening={false} />
                    <Circle x={hp[0]} y={hp[1]} radius={8 / view.scale} fill="#0F52BA" stroke="#fff" strokeWidth={2 / view.scale} draggable data-testid="rotate-handle"
                      onMouseEnter={(e) => { e.target.getStage().container().style.cursor = "grab"; }}
                      onDragStart={() => { ed.pushHistory(snapshot()); rotRef.current = { c, base: activeRoof }; }}
                      onDragMove={(e) => rotateRoof(e.target)}
                      onDragEnd={(e) => { rotRef.current = null; e.target.position({ x: hp[0], y: hp[1] }); }} />
                  </Group>
                );
              })()}

              {/* travetti misurati della falda */}
              {p.roof_type !== "piano" && roofs.map((r) => {
                const lines = rafterLines(r, p.engine?.rafter_spacing);
                if (!lines.length) return null;
                return (
                  <Group key={`raf-${r.id}`} listening={false} clipFunc={(ctx) => { r.polygon.map(llToWorld).forEach((q, i) => (i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1]))); ctx.closePath(); }}>
                    {lines.map((ln, i) => <Line key={i} points={ln.map(llToWorld).flat()} stroke="#a16207" strokeWidth={1.2 / view.scale} dash={[6 / view.scale, 3 / view.scale]} opacity={0.85} />)}
                  </Group>
                );
              })}

              {/* ombre degli ostacoli (21 dic, 12:00) */}
              {ed.showShadows && layout.fields.map((f) => f.shadows.length > 0 && (
                <Group key={`sh-${f.roof.id}`} listening={false} clipFunc={(ctx) => { f.roof.polygon.map(llToWorld).forEach((q, i) => (i ? ctx.lineTo(q[0], q[1]) : ctx.moveTo(q[0], q[1]))); ctx.closePath(); }}>
                  {f.shadows.map((sh, i) => <Line key={i} points={sh.pts.map((q) => llToWorld(f.frame.toLL(q))).flat()} closed fill="rgba(2,6,23,0.38)" />)}
                </Group>
              ))}

              {/* moduli (fase modules) */}
              {phase === "modules" && layout.fields.map((f) =>
                f.cells.map((c) => {
                  const pts = rectToLL(c.rect, f.frame).map(llToWorld);
                  const fill = c.active ? (ed.showZones ? ZONE[c.zone] : "#0F52BA") : "rgba(255,255,255,0.08)";
                  const sh = c.shaded && c.active && ed.showShadows;
                  return <Line key={`${c.fid}-${c.key}-${c.active}-${ed.showZones}-${sh}`} points={pts.flat()} closed
                    fill={sh ? "#475569" : fill} stroke={sh ? "#fb923c" : "#e0f2fe"} strokeWidth={(sh ? 2 : 0.6) / view.scale} dash={c.active ? undefined : [3 / view.scale, 3 / view.scale]}
                    onMouseDown={(e) => { if (ed.tool === "delete-module") { e.cancelBubble = true; toggleCell(c); } }} />;
                })
              )}

              {/* campi moduli: selezione, spostamento, riduzione a moduli interi, vertici */}
              {phase === "modules" && (
                <FieldLayer layout={layout} sel={ed.selField} scale={view.scale} llToWorld={llToWorld} canEdit={ed.tool === "select"}
                  toLL={(w) => { const m = prj.worldToM(w.x, w.y); return prj.toLL(m[0], m[1]); }}
                  onSelect={(rid, i) => { ed.setActiveRoof(rid); ed.setSelField({ rid, i }); }}
                  onCommit={(roof, i, poly) => { commit((prev) => ({ roofs: prev.roofs.map((r) => (r.id === roof.id ? setFields(r, editableFields(r).map((a, j) => (j === i ? poly : a)), i) : r)) })); }} />
              )}

              {/* quote dei gruppi di moduli e linee di sezione */}
              {phase === "modules" && (
                <PlanMarks layout={layout} flat={p.roof_type === "piano"} scale={view.scale} llToWorld={llToWorld} showGroups={ed.showGroupDims} onHidden={setHiddenDims}
                  fixedBoxes={ed.showDims && activeRoof ? activeRoof.polygon.map((ll, i, P) => {
                    const a = llToWorld(ll), b = llToWorld(P[(i + 1) % P.length]);
                    const t = `${fmt(dist(prj.toM(ll[0], ll[1]), prj.toM(P[(i + 1) % P.length][0], P[(i + 1) % P.length][1])), 2)} m`, w = t.length * 7.3 + 8;
                    return { x: ((a[0] + b[0]) / 2) * view.scale + w / 2, y: ((a[1] + b[1]) / 2) * view.scale + 10, w, h: 20 };
                  }) : []}
                  canDrag={ed.tool === "select"} onSectionStart={() => ed.pushHistory(snapshot())}
                  onSectionMove={(f, pt) => {
                    const m = prj.worldToM(pt.x, pt.y), u = f.frame.toUV(prj.toLL(m[0], m[1]))[0];
                    const us = f.polyUV.map((q) => q[0]), uc = Math.min(Math.max(u, Math.min(...us) + 0.01), Math.max(...us) - 0.01);
                    update((prev) => ({ roofs: prev.roofs.map((r) => (r.id === f.roof.id ? { ...r, section: { ...(r.section || {}), u: uc } } : r)) }));
                  }} />
              )}

              {/* quote dei lati del tetto attivo, sopra moduli, ostacoli e ombre */}
              {ed.showDims && activeRoof && activeRoof.polygon.length >= 2 && activeRoof.polygon.map((ll, i, P) => {
                const a = llToWorld(ll), b = llToWorld(P[(i + 1) % P.length]);
                const ma = prj.toM(ll[0], ll[1]), mb = prj.toM(P[(i + 1) % P.length][0], P[(i + 1) % P.length][1]);
                return <Dim key={`ed-${i}`} p1={a} p2={b} s={view.scale} text={`${fmt(dist(ma, mb), 2)} m`} listening={!(phase === "modules" && ed.selField)}
                  onClick={(e) => { e.cancelBubble = true; ed.setEditEdge({ rid: activeRoof.id, i }); }} />;
              })}

              {/* struttura calcolata dal motore: binari, ganci, zavorra (ganci e binari trascinabili) */}
              {phase === "modules" && ed.struct?.drawing && (
                <StructureLayer drawing={ed.struct.drawing} stale={structStale} busy={ed.busy} scale={view.scale} llToWorld={llToWorld} prj={prj}
                  fields={layout.fields} snap={ed.snap} rafter={Number(p.engine?.rafter_spacing) || 0} canDrag={ed.tool === "select" && !structStale && !ed.busy}
                  onCommit={(roofs) => { commit({ roofs }); runStructure({ ...p, roofs }, ed); }} roofs={roofs} />
              )}

              {/* draft disegno */}
              {ed.draft.length > 0 && (() => {
                const dp = ed.draft.map(prj.mToWorld);
                const hov = ed.hover ? prj.mToWorld(ed.hover) : null;
                const line = hov ? [...dp, hov] : dp;
                return (
                  <Group>
                    <Line points={line.flat()} stroke="#38bdf8" strokeWidth={2 / view.scale} dash={[6 / view.scale, 6 / view.scale]} />
                    {hov && ed.tool === "draw-rect3" && ed.draft.length === 2 && (
                      <Line points={rect3(ed.draft[0], ed.draft[1], ed.hover).map(prj.mToWorld).flat()} closed stroke="#38bdf8" strokeWidth={1.5 / view.scale} fill="rgba(56,189,248,0.25)" />
                    )}
                    {dp.length >= 2 && (ed.tool === "draw-rect3" || ed.tool === "draw-roof") && (
                      <Line points={[...dp[0], ...dp[1]]} stroke="#f59e0b" strokeWidth={3 / view.scale} />
                    )}
                    {hov && ed.tool.startsWith("draw-r") && ed.draft.length >= 1 && (
                      <Dim p1={dp[dp.length - 1]} p2={hov} s={view.scale} text={`${fmt(dist(ed.draft[ed.draft.length - 1], ed.hover), 2)} m`} />
                    )}
                    {ed.tool === "draw-obstacle" && hov && ed.draft.length === 1 && (
                      <Line points={rectWorld(dp[0], hov)} closed stroke="#f59e0b" strokeWidth={1.5 / view.scale} fill="rgba(245,158,11,0.35)" />
                    )}
                    {dp.map((pt, i) => <Circle key={i} x={pt[0]} y={pt[1]} radius={5 / view.scale} fill="#38bdf8" stroke="#fff" strokeWidth={1.5 / view.scale} />)}
                  </Group>
                );
              })()}
            </Layer>
          </Stage>
          <ToolRail ed={ed} phase={phase} roofType={p.roof_type} onField={fieldAction} hasSelField={!!ed.selField} />
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[400] bg-white/90 backdrop-blur-md border border-slate-200 rounded-md px-3 py-1.5 text-xs text-slate-600 shadow-sm" data-testid="editor-hint">
            {HINTS[ed.tool] || tr("Trascina col tasto destro per spostare, rotella per lo zoom.")}
          </div>
          {phase === "modules" && ed.showGroupDims && hiddenDims > 0 && (
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-[400] bg-slate-900/80 text-white rounded px-2.5 py-1 text-[11px]" data-testid="editor-hidden-dims">
              {hiddenDims} {hiddenDims === 1 ? tr("quota nascosta") : tr("quote nascoste")}{tr(": ingrandisci per vederle")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// similitudine da 2 punti: pixel della foto → coordinate del disegno (scala, rotazione, traslazione)
const BgPhoto = ({ bg, llToWorld, center, ppm, onError }) => {
  const [img, setImg] = useState(null);
  useEffect(() => {
    const im = new window.Image();
    im.crossOrigin = "anonymous";
    im.onload = () => { setImg(im); onError(false); };
    im.onerror = () => { setImg(null); onError(true); };
    im.src = fileUrl(bg.file_id);
    return () => { im.onload = im.onerror = null; };
  }, [bg.file_id]); // eslint-disable-line
  if (!img) return null;
  const T = georefTransform(bg, llToWorld);
  let { s, rot = 0, x, y } = T || {};
  if (!T) {
    s = (40 * ppm) / (bg.w || img.width);
    x = center[0] - (s * (bg.w || img.width)) / 2; y = center[1] - (s * (bg.h || img.height)) / 2;
  }
  return <KImage image={img} x={x} y={y} scaleX={s} scaleY={s} rotation={(rot * 180) / Math.PI} opacity={bg.opacity ?? 0.7} />;
};

const TileImg = ({ t }) => {
  const img = useImg(t.url);
  if (!img) return null;
  return <KImage image={img} x={t.x} y={t.y} width={256} height={256} />;
};

const HINTS = {
  section: tr("Linea di sezione: clicca un punto sulla falda, la linea si traccia perpendicolare alla gronda (una per falda). Poi trascinala per spostarla."),
  georef: tr("Georeferenziazione: clicca sulla mappa satellitare gli stessi 2 punti scelti sulla foto, nello stesso ordine. ESC annulla."),
  "draw-obstacle-poly": tr("Area esclusa poligonale: clicca i vertici, chiudi sul primo punto o con doppio clic. ESC annulla."),
  "draw-obstacle-line": tr("Ostacolo lineare (es. colmo, tubazione): clicca i punti, doppio clic per terminare. ESC annulla."),
  "draw-field": tr("Campo moduli: disegna l'area da occupare dentro il tetto (chiudi sul primo punto o con doppio clic)."),
  "draw-rect3": tr("Rettangolo: 1° clic inizio gronda, 2° clic fine gronda, muovi per la profondità della falda e 3° clic per confermare. ESC annulla."),
  "draw-roof": tr("Clicca per aggiungere i vertici. La prima linea è la gronda. Doppio clic o clic sul primo punto per chiudere. ESC annulla."),
  "draw-obstacle": tr("Clicca due angoli opposti per creare un ostacolo rettangolare."),
  "delete-module": tr("Clicca un modulo per toglierlo o rimetterlo."),
  select: tr("Clicca un tetto per selezionarlo; trascina i vertici per modificarlo."),
  pan: tr("Trascina per spostare la vista."),
  "move-field": tr("Trascina sul campo per spostare la griglia dei moduli."),
};

const CARD = (az) => ["Sud", tr("Sud-Ovest"), tr("Ovest"), tr("Nord-Ovest"), tr("Nord"), tr("Nord-Est"), "Est", tr("Sud-Est")][((Math.round(az / 45) % 8) + 8) % 8];

const rectWorld = (a, b) => [[a[0], a[1]], [b[0], a[1]], [b[0], b[1]], [a[0], b[1]]].flat();


function pointInPoly([x, y], poly) {
  let r = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) r = !r;
  }
  return r;
}
